import { FC, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Flex, Modal, Select, Input, Form, notification, Spin } from "antd";

import { TableLayoutRef } from "@/styles/STableLayout";
import { ChangeEnum, ConfirmedEnum, OperateEnum } from "@/types/inventory";
import apis from "@/apis/purchase";
import { RootState } from "@/store";
import { WarehouseContext } from "../../context/warehouseContext";
import CodesShow from "../../components/CodeShow";
import useInventoryBarcodes from "./hooks/useInventoryBarcodes";
import { Product, StoreTypeEnum } from "@/types/product";
import { InventoryHeader } from "./components/InventoryHeader";
import { orderFetch } from "@/apis/inventoryv2";
import { orderFetch as shipOrderApi } from "@/apis/shipment";

import InboundOrderForm from "./components/InboundOrderForm";
import reqApis from "@/apis/requirement"
import SpecialOut from "@/pages/Requirement/pages/Detail/components/SpecialOut";
import InventoryManage from "../InventoryManage";
import OutboundOrderForm from "./components/OutboundOrderForm";
import PurItemInventoryList from "./components/PurItemInventoryList";
import { PurchaseOrderItem } from "@/types/purchase";
import ManulInbound from "./components/ManulInbound";
import OtherOutBound from "./components/OtherOutBound";
import { UserPermissionEnum } from "@/types/user";
import { hasPermission } from "@/utils/user";
import { Shipment } from "@/types/shipment";
import ShipmentOutForm from "./components/ShipmentOutForm";
import { SourceTypeEnum } from "@/types/common";

interface FormFields {
  orderType: ChangeEnum;
  orderNumber: string;
}

const InventoryView: FC = () => {
  const { t } = useTranslation();
  const { id, categoryName } = useContext(WarehouseContext);
  const curUser = useSelector((state: RootState) => state.userReducer.curUser);
  const tableRef = useRef<TableLayoutRef | null>(null);

  const [keyword, setKeyword] = useState<string>("");
  const [inOutLoading, setInOutLoading] = useState<ChangeEnum | undefined>(undefined);
  const [open, setOpen] = useState<ChangeEnum | undefined>(undefined);
  const [openBarcode, setOpenBarcode] = useState<boolean>(false);
  const [barcodes, setBarcodes] = useState<Array<string>>([]);
  const [barcodeLoading, { getCodes }] = useInventoryBarcodes();

  const [onSelectOpen, setOnSelectOpen] = useState<ChangeEnum.IN | ChangeEnum.OUT | undefined>(undefined)
  const [sourceId, setSourceId] = useState<string | undefined>(undefined);
  const [purOrder, setPurOrder] = useState<PurchaseOrderItem | null>(null);
  const [purPro, setPurPro] = useState<Product | null>(null);
  const [form] = Form.useForm<FormFields>()
  const [type, setType] = useState<StoreTypeEnum>(StoreTypeEnum.CODE)
  const [formLoading, setFormLoading] = useState<boolean>(false)
  const [needNumber, setNeedNumber] = useState<boolean>(true);
  const [specialOut, setSpecialOut] = useState<{ id: string; number: string } | null>(null);
  const [openManulInbound, setOpenManulInbound] = useState<ChangeEnum.TEMP_IN | ChangeEnum.PROD_IN | ChangeEnum.PROD_TEMP_IN | undefined>(undefined);
  const [openOtherOutbound, setOpenOtherOutbound] = useState<boolean>(false);
  const [openShipOut, setOpenShipOut] = useState<boolean>(false);
  const [shipOutData, setShipOutData] = useState<Shipment | null>(null);


  // 当打开入库/出库弹窗时，设置默认值
  useEffect(() => {
    if (onSelectOpen === ChangeEnum.IN) {
      // 设置默认值为送货单入库（预入库）
      form.setFieldsValue({
        orderType: ChangeEnum.PRE_IN
      });
      setNeedNumber(true);
    } else if (onSelectOpen === ChangeEnum.OUT) {
      // 设置默认值为领料单出库（预出库）
      form.setFieldsValue({
        orderType: ChangeEnum.PRE_OUT
      });
      setNeedNumber(true);
    }
  }, [onSelectOpen]);


  // 处理单证编号输入：全角转半角、小写转大写
  const handleDocumentNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = (e.target.value || '').trim();
    // 全角转半角
    let converted = value.replace(/[\u3000-\u303F\uFF00-\uFFEF]/g, (char) => {
      const code = char.charCodeAt(0);
      // 全角空格转半角空格
      if (code === 0x3000) return '\u0020';
      // 全角字符转半角字符
      if (code >= 0xFF01 && code <= 0xFF5E) return String.fromCharCode(code - 0xFEE0);
      return char;
    });
    // 转大写
    converted = converted.toUpperCase();
    // 更新表单值
    form.setFieldsValue({ orderNumber: converted });
  };

  const onCancel = () => {
    setOnSelectOpen(undefined)
    form.resetFields()
    setNeedNumber(true)
    setOpenShipOut(false);
    setShipOutData(null);
  }

  const onOk = () => {
    form.validateFields().then((values) => {
      if (values.orderType && [ChangeEnum.IN, ChangeEnum.PRE_IN, ChangeEnum.OUT, ChangeEnum.PRE_OUT, ChangeEnum.SALES_OUT].includes(values.orderType)) {
        // 预定单入库
        setFormLoading(true)
        orderFetch(values.orderNumber).then(res => {
          const arr = { ...res }
          if (values.orderType === ChangeEnum.SALES_OUT && (arr.type !== ChangeEnum.PRE_OUT || arr.opType !== OperateEnum.TRANSACTION)) {
            notification.error({
              placement: 'top',
              message: t('warehouse.numberNotExist'),
            })
            setFormLoading(false)
            return
          }
          if (values.orderType === ChangeEnum.PRE_OUT && arr.type === ChangeEnum.PRE_OUT && arr.opType === OperateEnum.TRANSACTION) {
            notification.error({
              placement: 'top',
              message: t('warehouse.numberNotExist'),
            })
            setFormLoading(false)
            return
          }
          if ([ChangeEnum.IN, ChangeEnum.PRE_IN].includes(values.orderType) && [ChangeEnum.OUT, ChangeEnum.PRE_OUT, ChangeEnum.TEMP_OUT, ChangeEnum.SALES_OUT].includes(arr.type)) {
            notification.error({
              placement: 'top',
              message: t('warehouse.numberNotExist'),
            })
            setFormLoading(false)
            return
          }
          if ([ChangeEnum.OUT, ChangeEnum.PRE_OUT].includes(values.orderType) && [ChangeEnum.IN, ChangeEnum.PRE_IN, ChangeEnum.ORDER_IN, ChangeEnum.TEMP_IN].includes(arr.type)) {
            notification.error({
              placement: 'top',
              message: t('warehouse.numberNotExist'),
            })
            setFormLoading(false)
            return
          }
          if (arr.confirmState === ConfirmedEnum.CONFIRM) {
            notification.error({
              placement: 'top',
              message: t('warehouse.orderConfirmed'),
            })
            setFormLoading(false)
            return
          }
          setSourceId(arr.id)
          setOpen(arr.type);
          // setType(arr[0].type === 1 ? StoreTypeEnum.CODE : StoreTypeEnum.NUM)
          setFormLoading(false)
          onCancel()
        }).catch(err => {
          console.log('err', err)
          setFormLoading(false)
          notification.error({ message: t('warehouse.noOrder'), description: values.orderNumber })
        })
      } else if (values.orderType === ChangeEnum.TEMP_OUT) {
        setOpenOtherOutbound(true);
        onCancel()
      } else if (values.orderType === ChangeEnum.PROD_IN || values.orderType === ChangeEnum.TEMP_IN || values.orderType === ChangeEnum.PROD_TEMP_IN) {
        setOpenManulInbound(values.orderType);
        onCancel()
      } else if (values.orderType === ChangeEnum.ORDER_IN) {
        let params = {
          supplierId: '',
          keyword: values.orderNumber,
        }
        // 采购单入库
        setFormLoading(true)
        apis.itemList({ body: params }).then(orderList => {
          const order = orderList?.body?.filter(item => item.number === values.orderNumber)
          if (order?.length) {
            if (order[0].order.acceptState !== 1) {
              notification.error({
                placement: 'top',
                message: t('warehouse.purchaseNotPass'),
              })
              setFormLoading(false)
              return
            }
            setPurOrder(order[0])
            setPurPro(order[0].product)
            onCancel()
          } else {
            notification.error({
              placement: 'top',
              message: t('warehouse.numberNotExist'),
            })
          }
          setFormLoading(false)
        }).catch(() => setFormLoading(false))
      } else if (values.orderType === ChangeEnum.SPEC_OUT) {
        setFormLoading(true)
        reqApis.list({ body: { keyword: values.orderNumber ?? '', states: [], status: 1 } })
          .then(res => {
            if (Array.isArray(res?.body) && res.body.length > 0) {
              setSpecialOut({ id: res.body[0].id, number: res.body[0].number })
            } else {
              notification.error({
                placement: 'top',
                message: t('warehouse.numberNotExist'),
              })
            }
          })
          .catch(() => {
            notification.error({
              placement: 'top',
              message: t('warehouse.numberNotExist'),
            })
          })
          .finally(() => setFormLoading(false))
      } else if (values.orderType === ChangeEnum.SHIPMENT_OUT) {
        setFormLoading(true);
        orderFetch(values.orderNumber)
          .then(res => {
            if (res.sourceType === SourceTypeEnum.SHIPMETN && res.opType === OperateEnum.SHIPMENT_OUT) {
              if (res.confirmState === ConfirmedEnum.CONFIRM) return notification.error({
                placement: 'top',
                message: t('warehouse.orderConfirmed'),
              })
              setSourceId(res.id)
              setOpen(res.type);
              setFormLoading(false)
              onCancel()
            } else notification.error({
              placement: 'top',
              message: t('warehouse.numberNotExist'),
            })
          })
          .catch(err => {
            notification.error({ message: t('warehouse.noShipment'), description: values.orderNumber })
          })
          .finally(() => setFormLoading(false))
      } else {
        // 销售单出库
        // setFormLoading(true)
        // approvalList({
        //   state: CommonStateEnum.ACTIVE,
        //   params: {
        //     body: { customerId: '', keyword: values.orderNumber },
        //   },
        //   orgId: curOrg.id,
        // }
        // ).then(res => {
        //   if (res.body?.length) {
        //     setSourceId(values.orderNumber)
        //     setOpen(values.orderType);
        //     setType(StoreTypeEnum.NUM)
        //     onCancel()
        //   } else {
        //     notification.error({
        //       placement: 'top',
        //       message: t('warehouse.numberNotExist'),
        //     })
        //   }
        //   setFormLoading(false)
        // }).catch(() => setFormLoading(false))
      }
    }, () => {
      // 表单会展示必填提示，处理校验失败以避免未捕获的 Promise rejection。
    })
  }

  const handleInboundOrder = (orderId: string) => {
    setSourceId(orderId)
    setOpen(ChangeEnum.PRE_IN);
  };

  const options: { label: string, value: ChangeEnum }[] = useMemo(() => {
    let allOptions: {
      type: ChangeEnum, permission: UserPermissionEnum | undefined, label: string, value: ChangeEnum
    }[] = [
        { type: ChangeEnum.IN, permission: undefined, label: t('warehouse.preInOrder'), value: ChangeEnum.PRE_IN }, // 送货单入库（预入库）
        { type: ChangeEnum.IN, permission: UserPermissionEnum.TEMP_INBOUND, label: t('warehouse.tempIn'), value: ChangeEnum.TEMP_IN, },// 临时入库单入库
        { type: ChangeEnum.IN, permission: undefined, label: t('warehouse.proIn'), value: ChangeEnum.PROD_IN },// 生产入库
        { type: ChangeEnum.IN, permission: UserPermissionEnum.TEMP_INBOUND_PRODUCT, label: t('warehouse.prodTempIn'), value: ChangeEnum.PROD_TEMP_IN },// 生产入库
        { type: ChangeEnum.OUT, permission: undefined, label: t('warehouse.preOutOrder'), value: ChangeEnum.PRE_OUT },
        { type: ChangeEnum.OUT, permission: undefined, label: t('order.saleOut'), value: ChangeEnum.SALES_OUT },
        // { type: ChangeEnum.OUT, permission: undefined, label: t('warehouse.shipmentOut'), value: ChangeEnum.SHIPMENT_OUT },
        { type: ChangeEnum.OUT, permission: UserPermissionEnum.OTHER_OUTBOUND, label: t('warehouse.otherOut'), value: ChangeEnum.TEMP_OUT },
      ];
    return allOptions.filter(o => o.type === onSelectOpen && (o.permission === undefined || hasPermission(curUser, [o.permission])))
  }, [onSelectOpen, t, curUser]);

  return (
    <Flex vertical style={{ width: "100%", height: "100%" }}>
      <InventoryManage header={
        <InventoryHeader
          inOutLoading={inOutLoading}
          setInOutLoading={setInOutLoading}
          keyword={keyword}
          setKeyword={setKeyword}
          tableRef={tableRef}
          fetch={() => { }}
          setOpen={setOpen}
          type={type}
          categoryName={categoryName}
          setOnOpen={value => setOnSelectOpen(value)}
        />
      } />

      <CodesShow
        loading={barcodeLoading}
        barCodes={barcodes}
        open={openBarcode}
        onCancel={() => setOpenBarcode(false)}
      />
      {/* 采购入库单据列表 */}
      {
        purOrder?.id && purPro && <PurItemInventoryList purItemId={purOrder.id} purOrderNo={purOrder.number} product={purPro} onClose={() => setPurOrder(null)} onInbound={handleInboundOrder} />
      }
      {/* 入库 */}
      {
        open && [ChangeEnum.IN, ChangeEnum.PRE_IN].includes(open) &&
        <InboundOrderForm sourceId={sourceId ?? ''} onCancel={() => { setSourceId(''); setOpen(undefined) }} />
      }
      {/* 出库 */}
      {
        open && [ChangeEnum.OUT, ChangeEnum.PRE_OUT].includes(open) &&
        <OutboundOrderForm sourceId={sourceId ?? ''} onCancel={() => { setSourceId(''); setOpen(undefined) }} />
      }
      {/* 专用料出库 */}
      {!!specialOut && <SpecialOut reqId={specialOut.id} reqNumber={specialOut.number} visible={!!specialOut} onClose={() => setSpecialOut(null)} />}
      {/* 选择出入库类型 */}
      {onSelectOpen && <Modal
        title={onSelectOpen === ChangeEnum.IN ? t('warehouse.inbound') : t('warehouse.outbound')}
        open={!!onSelectOpen}
        width={520}
        onCancel={() => {
          onCancel()
        }}
        onOk={onOk}
        destroyOnClose
      >
        <Spin spinning={formLoading}>
          <Form<FormFields> labelCol={{ span: 5 }} form={form}>
            <Form.Item<FormFields> label={onSelectOpen === ChangeEnum.IN ? t('warehouse.inboundType') : t('warehouse.outboundType')} name="orderType" rules={[{ required: true, message: t('warehouse.selectOrderType') }]}>
              <Select
                onChange={(value) => {
                  if ([ChangeEnum.TEMP_OUT, ChangeEnum.TEMP_IN, ChangeEnum.PROD_IN, ChangeEnum.PROD_TEMP_IN].includes(value)) {
                    form.setFieldsValue({ orderNumber: '' })
                    setNeedNumber(false)
                  } else {
                    setNeedNumber(true)
                  }
                }}
                style={{ width: 300 }}
                options={options}
              />
            </Form.Item>
            {
              needNumber && (
                <Form.Item<FormFields> label={t('warehouse.documentNumber')} name="orderNumber" rules={[{ required: true, message: t('warehouse.selectOrderNumber') }]}>
                  <Input style={{ width: 300 }} onChange={handleDocumentNumberChange} />
                </Form.Item>
              )
            }
          </Form>
        </Spin>
      </Modal>}
      {openManulInbound !== undefined && (
        <ManulInbound openType={openManulInbound} onCancel={() => setOpenManulInbound(undefined)} />
      )}
      {openOtherOutbound && (
        <OtherOutBound open={openOtherOutbound} onCancel={() => setOpenOtherOutbound(false)} />
      )}
      {/* {openShipOut && !!shipOutData && <ShipmentOutForm open onClose={() => {
        setOpenShipOut(false); setShipOutData(null)
      }} onSuccess={() => { }} shipment={shipOutData}
      />} */}
    </Flex>
  );
};

export default InventoryView;
